import * as React from 'react';

import { countdownHorizonSeconds, secondsPerDay, secondsPerHour, secondsPerMinute } from '../consts';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// At most two units, largest first, with leading zeroes dropped: "25d 17h",
// "17h 59m", "59m 30s". Three equal-weight numbers read as three separate
// figures rather than one duration, and they were the wrong three at both
// ends of the scale - minutes ticking pointlessly on something a month out,
// and a dead "0d 0h" prefix on something happening within the hour. Dropping
// to two means the units on the row always describe the scale you're on, and
// seconds appear exactly when they've started to matter.
function durationParts(totalSeconds: number): string[] {
    const days = Math.floor(totalSeconds / secondsPerDay);
    const hours = Math.floor((totalSeconds % secondsPerDay) / secondsPerHour);
    const minutes = Math.floor((totalSeconds % secondsPerHour) / secondsPerMinute);
    const seconds = Math.floor(totalSeconds % secondsPerMinute);

    if (days > 0) return [`${days}d`, `${hours}h`];
    if (hours > 0) return [`${hours}h`, `${minutes}m`];
    return [`${minutes}m`, `${seconds}s`];
}

const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "25d" tells you how long; it doesn't tell you what you'd be looking at in a
// calendar, which is the thing you actually plan against. "today" and
// "tomorrow" win over a date for the two cases you act on, and a past event
// gets a date so a yellow bar still says which day it was. (Rounded, not
// floored: a DST boundary makes the difference 23 or 25 hours.)
function dateLabel(target: Date, nowSeconds: number): string {
    const dayDelta = Math.round(
        (startOfDay(target) - startOfDay(new Date(nowSeconds * 1000))) / (secondsPerDay * 1000));
    if (dayDelta === 0) return 'today';
    if (dayDelta === 1) return 'tomorrow';
    if (dayDelta === -1) return 'yesterday';
    return `${DAY_NAMES[target.getDay()]} ${MONTH_NAMES[target.getMonth()]} ${target.getDate()}`;
}

interface CountdownClockProps {
    targetDate : Date;
    eventName: string;
    emoji?: string;
    viewType: 'string' | 'bar';
    index: number;
    onTimeUpdate : (lastTime : number, time : number) => void;
}

interface CountdownClockState {
    now : number;
    timeout: NodeJS.Timeout;
}

export default class CountdownClock extends React.Component<CountdownClockProps, CountdownClockState> {
    static defaultProps = {
        viewType: 'bar',
        index: 0,
    }

    constructor(props : CountdownClockProps) {
        super(props);

        this.pad = this.pad.bind(this);
        this.cleanStr = this.cleanStr.bind(this);
        this.setTime = this.setTime.bind(this);
        this.tick = this.tick.bind(this);

        this.state = { now : 0, timeout: null };
    }

    pad(str: string, len: number): string {
        return '0'.repeat(Math.max(0, len - str.length)) + str;
    }

    cleanStr(num : number): string {
        return this.pad(num.toFixed(0), 2);
    }

    setTime(now: number): void {
        const lastTime = this.state.now;

        this.setState({ now }, () => {
            this.props.onTimeUpdate(lastTime, now);
        });
    }

    tick(): void {
        this.setState({ timeout: setTimeout(this.tick, 16) });
        const now = (new Date()).getTime() / 1000;
        this.setTime(now);
    }

    componentDidMount(): void {
        this.tick();
    }

    componentWillUnmount(): void {
        if (this.state.timeout !== null) {
            clearTimeout(this.state.timeout);
        }
    }

    render(): JSX.Element {
        const targetTime = this.props.targetDate.getTime() / 1000;
        const now = this.state.now;

        const past = targetTime < now;

        let remaining = Math.floor(targetTime - now);
        const days = Math.floor(remaining / secondsPerDay);
        remaining %= secondsPerDay;
        const hours = Math.floor(remaining / secondsPerHour);
        remaining %= secondsPerHour;
        const minutes = Math.floor(remaining / secondsPerMinute);
        remaining %= secondsPerMinute;
        const seconds = Math.floor(remaining);

        let content: string | JSX.Element;
        switch (this.props.viewType) {
            case 'string':
                content = `${this.cleanStr(days)}:${this.cleanStr(hours)}:${this.cleanStr(minutes)}:${this.cleanStr(seconds)}`;
            case 'bar':
                const eventNameText = `${this.props.eventName}`;
                const whenText = dateLabel(this.props.targetDate, now);

                const widthPercent = past ? 100 : Math.max(0, Math.min(100,
                    (1 - (targetTime - now) / countdownHorizonSeconds) * 100));
                // Keying each number on its own text remounts the node when the
                // value changes, which replays the pop animation.
                const num = (text: string) =>
                    <div className="clock-bar-text juice-num" key={text}>{text}</div>;
                // Both layers hold identical content so they stay aligned; the
                // fill layer is clipped to the progress width.
                //
                // The clip is a clip-path rather than a narrower box, and that
                // matters for more than tidiness. When the fill layer was sized
                // to the progress percentage, its children sat in a narrower
                // flex container than the base layer's, so every one of them had
                // to be flex-shrink: 0 or the two layers would disagree about
                // where the text starts. That made the event name unshrinkable,
                // and a name wider than the bar didn't ellipsize - it ran under
                // the border and lost its last glyph to overflow: hidden ("The
                // Witcher 3 Remastered" printed as "Remastere"). Laying both
                // layers out at the full width and clipping the fill visually
                // lets the name shrink in both layers by the same amount.
                const barContent = (
                    <>
                        {/* A past event shows no countdown at all - the yellow
                            bar and the date on the right already say it's been
                            and gone, and "0d 0h" said nothing. */}
                        {!past && durationParts(targetTime - now).map(num)}
                        {/* Its own span rather than part of the name string:
                            emoji are colour glyphs and ignore the fill layer's
                            colour swap, so they need to be sized and spaced
                            independently of the text they sit beside. */}
                        {this.props.emoji &&
                            <div className="clock-bar-emoji">{this.props.emoji}</div>}
                        <div className="clock-bar-text clock-bar-name">{eventNameText}</div>
                        {/* Last in the row and flex-grown into by the name, so
                            it lands hard against the right border on every bar
                            and the dates line up as a column down the list. */}
                        <div className="clock-bar-when">{whenText}</div>
                    </>
                );
                content = (
                    <div className={`outer-clock-bar${past ? ' past' : ''}`} style={{ animationDelay: `${-this.props.index * 0.83}s` }}>
                        <div className="clock-bar-layer clock-bar-base">{barContent}</div>
                        <div
                            className={`clock-bar-layer clock-bar-fill${past ? ' past' : ''}`}
                            style={{ clipPath: `inset(0 ${100 - widthPercent}% 0 0)` }}>
                            {barContent}
                        </div>
                    </div>
                );
        }

        return (
            <div className="countdown-clock" style={{ animationDelay: `${this.props.index * 0.09}s` }}>
                {content}
            </div>
        );
    }
}
